/**
 * Native Bridge 工具
 * 用于与原生应用进行通信
 */

import { UserInfo } from './auth';

/**
 * 通知原生端司机登录成功
 * @param userInfo 用户信息
 */
export const notifyNativeDriverLogin = (userInfo: UserInfo): void => {
  try {
    const driverId = userInfo.account.id;
    const accountRole = userInfo.account.role.name;
    const accountToken = userInfo.token;
    const accountName = userInfo.account.name;

    // iOS WebKit
    if (window.webkit?.messageHandlers?.onDriverLogin?.postMessage) {
      window.webkit.messageHandlers.onDriverLogin.postMessage({
        driverId: driverId,
        accountName: accountName,
        accountRole: accountRole,
        accountToken: accountToken,
        timestamp: Date.now(),
      });
      console.log("[Native Bridge] 已通知iOS端司机登录, driverId:", driverId);
    }

    // Android
    if (window.AndroidApp?.onDriverLogin) {
      window.AndroidApp.onDriverLogin(driverId, accountRole, accountToken, accountName, Date.now());
      console.log('[Native Bridge] 已通知Android端司机登录, driverId:', driverId);
    }
  } catch (error) {
    console.error('[Native Bridge] 通知原生端司机登录失败:', error);
  }
};

/**
 * 通知原生端司机登出成功
 * @param driverId 司机ID
 */
export const notifyNativeDriverLogout = (driverId: string): void => {
  try {
    // iOS WebKit
    if (window.webkit?.messageHandlers?.onDriverLogout?.postMessage) {
      window.webkit.messageHandlers.onDriverLogout.postMessage({
        driverId: driverId,
        timestamp: Date.now()
      });
      console.log('[Native Bridge] 已通知iOS端司机登出, driverId:', driverId);
    }

    // Android
    if (window.AndroidApp?.onDriverLogout) {
      window.AndroidApp.onDriverLogout(driverId);
      console.log('[Native Bridge] 已通知Android端司机登出, driverId:', driverId);
    }
  } catch (error) {
    console.error('[Native Bridge] 通知原生端司机登出失败:', error);
  }
};

/**
 * 从native端获取登录信息（web主动拉取）
 * 从8.8.0开始，登录走native端，web加载完成后主动获取
 */
export const getNativeUserInfo = (): Promise<UserInfo | null> => {
  return new Promise((resolve) => {
    // iOS WebKit
    if (window.webkit?.messageHandlers?.getNativeUserInfo?.postMessage) {
      const callbackName = `handleNativeUserInfo_${Date.now()}`;
      (window as any)[callbackName] = (userInfo: UserInfo | null) => {
        resolve(userInfo);
        setTimeout(() => { delete (window as any)[callbackName]; }, 5000);
      };
      window.webkit.messageHandlers.getNativeUserInfo.postMessage({ callback: callbackName });
      return;
    }

    // Android（与 MainActivity.WebAppInterface.getNativeUserInfo 对齐）
    if ((window as any).AndroidApp?.getNativeUserInfo) {
      const callbackName = `handleNativeUserInfo_${Date.now()}`;
      (window as any)[callbackName] = (userInfo: UserInfo | null) => {
        resolve(userInfo);
        setTimeout(() => {
          delete (window as any)[callbackName];
        }, 5000);
      };
      try {
        (window as any).AndroidApp.getNativeUserInfo(callbackName);
      } catch (_e) {
        resolve(null);
      }
      return;
    }

    resolve(null);
  });
};

/**
 * 请求native端执行退出登录
 * 从8.8.0开始，登录、退登走native端
 */
export const requestNativeLogout = (): void => {
  try {
    // iOS WebKit
    if (window.webkit?.messageHandlers?.requestNativeLogout?.postMessage) {
      window.webkit.messageHandlers.requestNativeLogout.postMessage({
        timestamp: Date.now()
      });
      console.log('[Native Bridge] 已请求iOS端执行退出登录');
    }

    // Android
    if (window.AndroidApp?.requestNativeLogout) {
      window.AndroidApp.requestNativeLogout();
      console.log('[Native Bridge] 已请求Android端执行退出登录');
    }
  } catch (error) {
    console.error('[Native Bridge] 请求原生端退出登录失败:', error);
  }
};

/**
 * 应用启动时同步用户登录信息到原生端
 * @param userInfo 用户信息
 */
export const syncUserInfoOnAppStart = (userInfo: UserInfo): void => {
  try {
    const driverId = userInfo.account.id;
    const accountRole = userInfo.account.role.name;
    const accountToken = userInfo.token;
    const accountName = userInfo.account.name;

    // iOS WebKit
    if (window.webkit?.messageHandlers?.onAppStartWithUserInfo?.postMessage) {
      window.webkit.messageHandlers.onAppStartWithUserInfo.postMessage({
        driverId: driverId,
        accountRole: accountRole,
        accountToken: accountToken,
        accountName: accountName,
        timestamp: Date.now()
      });
      console.log('[Native Bridge] 应用启动时已同步用户信息到iOS端, driverId:', driverId);
    }

    // Android (暂不实现，按用户要求)
    if (window.AndroidApp?.onAppStartWithUserInfo) {
      window.AndroidApp.onAppStartWithUserInfo(driverId, accountRole, accountToken, accountName);
      console.log('[Native Bridge] 应用启动时已同步用户信息到Android端, driverId:', driverId);
    }
  } catch (error) {
    console.error('[Native Bridge] 应用启动时同步用户信息失败:', error);
  }
};

